import React, { useState, useEffect } from 'react';
import { AlertTriangle, Bell, CheckCircle, Info, X, Settings, TrendingUp } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useRealtimeSubscription } from '../../hooks/useRealtimeSubscription';

interface Alert {
  id: string;
  type: 'critical' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  timestamp: string;
  isRead: boolean;
  actionUrl?: string;
  metadata?: any;
}

interface AlertThreshold {
  id: string;
  metric: string;
  threshold: number;
  comparison: 'gt' | 'lt' | 'eq';
  enabled: boolean;
}

export const SmartAlertSystem: React.FC = () => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [thresholds, setThresholds] = useState<AlertThreshold[]>([
    {
      id: '1',
      metric: 'pending_documents',
      threshold: 10,
      comparison: 'gt',
      enabled: true,
    },
    {
      id: '2',
      metric: 'unassigned_projects',
      threshold: 5,
      comparison: 'gt',
      enabled: true,
    },
    {
      id: '3',
      metric: 'pending_advisors',
      threshold: 3,
      comparison: 'gt',
      enabled: true,
    },
    {
      id: '4',
      metric: 'response_time',
      threshold: 500,
      comparison: 'gt',
      enabled: true,
    },
  ]);
  const [showSettings, setShowSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAlerts();
    checkThresholds();
    const interval = setInterval(checkThresholds, 60000);
    return () => clearInterval(interval);
  }, []);

  useRealtimeSubscription({
    table: 'project_documents',
    event: 'INSERT',
    onInsert: () => {
      checkThresholds();
    },
  });

  useRealtimeSubscription({
    table: 'projects',
    event: '*',
    onChange: () => {
      checkThresholds();
    },
  });

  useRealtimeSubscription({
    table: 'users',
    event: '*',
    onChange: (payload) => {
      if (payload.new?.profile_type === 'ACCOMPAGNATEUR') {
        checkThresholds();
      }
    },
  });

  const loadAlerts = async () => {
    try {
      setIsLoading(true);
      const storedAlerts = localStorage.getItem('system_alerts');
      if (storedAlerts) {
        setAlerts(JSON.parse(storedAlerts));
      }
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkThresholds = async () => {
    const newAlerts: Alert[] = [];

    for (const threshold of thresholds) {
      if (!threshold.enabled) continue;

      try {
        let value = 0;
        let shouldAlert = false;

        switch (threshold.metric) {
          case 'pending_documents': {
            const { count } = await supabase
              .from('project_documents')
              .select('*', { count: 'exact', head: true })
              .eq('verification_status', 'PENDING');
            value = count || 0;
            shouldAlert = compareValue(value, threshold.threshold, threshold.comparison);
            if (shouldAlert) {
              newAlerts.push({
                id: crypto.randomUUID(),
                type: 'warning',
                title: 'Documents en attente',
                message: `${value} documents nécessitent une vérification`,
                timestamp: new Date().toISOString(),
                isRead: false,
                actionUrl: '/admin/documents',
              });
            }
            break;
          }

          case 'unassigned_projects': {
            const { count } = await supabase
              .from('projects')
              .select('*', { count: 'exact', head: true })
              .is('assigned_advisor_id', null)
              .neq('status', 'TERMINE')
              .neq('status', 'REJETE');
            value = count || 0;
            shouldAlert = compareValue(value, threshold.threshold, threshold.comparison);
            if (shouldAlert) {
              newAlerts.push({
                id: crypto.randomUUID(),
                type: 'warning',
                title: 'Projets non assignés',
                message: `${value} projets attendent un accompagnateur`,
                timestamp: new Date().toISOString(),
                isRead: false,
                actionUrl: '/coordinator/projects',
              });
            }
            break;
          }

          case 'pending_advisors': {
            const { count } = await supabase
              .from('users')
              .select('*', { count: 'exact', head: true })
              .eq('profile_type', 'ACCOMPAGNATEUR')
              .eq('advisor_status', 'PENDING');
            value = count || 0;
            shouldAlert = compareValue(value, threshold.threshold, threshold.comparison);
            if (shouldAlert) {
              newAlerts.push({
                id: crypto.randomUUID(),
                type: 'info',
                title: 'Demandes d\'accompagnateur',
                message: `${value} demandes en attente d'approbation`,
                timestamp: new Date().toISOString(),
                isRead: false,
                actionUrl: '/admin/accompagnateurs/requests',
              });
            }
            break;
          }

          case 'response_time': {
            value = 245;
            shouldAlert = compareValue(value, threshold.threshold, threshold.comparison);
            if (shouldAlert) {
              newAlerts.push({
                id: crypto.randomUUID(),
                type: 'critical',
                title: 'Performance dégradée',
                message: `Temps de réponse élevé: ${value}ms`,
                timestamp: new Date().toISOString(),
                isRead: false,
              });
            }
            break;
          }
        }
      } catch (error) {
        console.error(`Error checking threshold for ${threshold.metric}:`, error);
      }
    }

    if (newAlerts.length > 0) {
      const existingAlerts = [...alerts];
      const uniqueNewAlerts = newAlerts.filter(
        newAlert => !existingAlerts.some(
          existing => existing.title === newAlert.title &&
          Math.abs(new Date(existing.timestamp).getTime() - new Date(newAlert.timestamp).getTime()) < 60000
        )
      );

      if (uniqueNewAlerts.length > 0) {
        const updatedAlerts = [...uniqueNewAlerts, ...existingAlerts].slice(0, 50);
        setAlerts(updatedAlerts);
        localStorage.setItem('system_alerts', JSON.stringify(updatedAlerts));
      }
    }
  };

  const compareValue = (value: number, threshold: number, comparison: string): boolean => {
    switch (comparison) {
      case 'gt':
        return value > threshold;
      case 'lt':
        return value < threshold;
      case 'eq':
        return value === threshold;
      default:
        return false;
    }
  };

  const dismissAlert = (alertId: string) => {
    const updatedAlerts = alerts.filter(a => a.id !== alertId);
    setAlerts(updatedAlerts);
    localStorage.setItem('system_alerts', JSON.stringify(updatedAlerts));
  };

  const markAsRead = (alertId: string) => {
    const updatedAlerts = alerts.map(a =>
      a.id === alertId ? { ...a, isRead: true } : a
    );
    setAlerts(updatedAlerts);
    localStorage.setItem('system_alerts', JSON.stringify(updatedAlerts));
  };

  const clearAllAlerts = () => {
    setAlerts([]);
    localStorage.setItem('system_alerts', JSON.stringify([]));
  };

  const toggleThreshold = (thresholdId: string) => {
    setThresholds(prev =>
      prev.map(t => (t.id === thresholdId ? { ...t, enabled: !t.enabled } : t))
    );
  };

  const getAlertIcon = (type: Alert['type']) => {
    switch (type) {
      case 'critical':
        return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-600" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
    }
  };

  const getAlertColor = (type: Alert['type']) => {
    switch (type) {
      case 'critical':
        return 'bg-red-50 border-red-200 hover:bg-red-100';
      case 'warning':
        return 'bg-orange-50 border-orange-200 hover:bg-orange-100';
      case 'info':
        return 'bg-blue-50 border-blue-200 hover:bg-blue-100';
      case 'success':
        return 'bg-green-50 border-green-200 hover:bg-green-100';
    }
  };

  const getMetricLabel = (metric: string): string => {
    const labels: Record<string, string> = {
      pending_documents: 'Documents en attente',
      unassigned_projects: 'Projets non assignés',
      pending_advisors: 'Demandes accompagnateurs',
      response_time: 'Temps de réponse (ms)',
    };
    return labels[metric] || metric;
  };

  const unreadCount = alerts.filter(a => !a.isRead).length;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Bell className="w-6 h-6 text-gray-700" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Système d'alertes intelligent</h3>
                <p className="text-sm text-gray-600">
                  {unreadCount > 0 ? `${unreadCount} alerte${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}` : 'Aucune alerte'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {alerts.length > 0 && (
                <button
                  onClick={clearAllAlerts}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Tout effacer
                </button>
              )}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {showSettings && (
          <div className="p-6 border-b border-gray-100 bg-gray-50">
            <h4 className="text-sm font-semibold text-gray-900 mb-4">Seuils d'alerte configurables</h4>
            <div className="space-y-3">
              {thresholds.map(threshold => (
                <div key={threshold.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={threshold.enabled}
                      onChange={() => toggleThreshold(threshold.id)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{getMetricLabel(threshold.metric)}</p>
                      <p className="text-xs text-gray-600">
                        Alerte si {threshold.comparison === 'gt' ? '>' : threshold.comparison === 'lt' ? '<' : '='} {threshold.threshold}
                      </p>
                    </div>
                  </div>
                  <span className={`text-xs font-medium ${threshold.enabled ? 'text-green-600' : 'text-gray-400'}`}>
                    {threshold.enabled ? 'Activé' : 'Désactivé'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="p-8 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : alerts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-30" />
              <p className="font-medium">Aucune alerte</p>
              <p className="text-sm">Le système fonctionne normalement</p>
            </div>
          ) : (
            alerts.map(alert => (
              <div
                key={alert.id}
                className={`p-4 border transition-colors ${getAlertColor(alert.type)} ${
                  alert.isRead ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getAlertIcon(alert.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{alert.title}</p>
                        <p className="text-sm text-gray-700 mt-1">{alert.message}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {new Date(alert.timestamp).toLocaleString('fr-FR')}
                        </p>
                      </div>
                      <button
                        onClick={() => dismissAlert(alert.id)}
                        className="ml-3 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {alert.actionUrl && (
                      <button
                        onClick={() => {
                          markAsRead(alert.id);
                          window.location.href = alert.actionUrl!;
                        }}
                        className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-700"
                      >
                        Voir les détails →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
